<template>
	<section class="order-summary">
		<h4 class="title">Order summary</h4>

		<div class="flex flex-col gap-6">
			<SummaryItem
				meta-label="Order ID"
				:meta-primary="checkout?.reference ?? null"
			/>

			<SummaryItem
				meta-label="Vehicle"
				:meta-primary="checkout?.vehicle.caption ?? null"
			/>

			<template v-if="hasCollapsibleDetails">
				<div
					id="order-summary-details"
					class="grid transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none sm:grid-rows-[1fr]"
					:class="isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
				>
					<div class="overflow-hidden">
						<div class="flex flex-col gap-6">
							<div v-if="driver?.first_name">
								<div class="flex flex-col gap-6 sm:flex-row">
									<SummaryItem
										class="w-1/2"
										meta-label="Primary driver"
										:meta-primary="primaryDriverName"
										:meta-secondary="driver.mobile_number"
									/>

									<SummaryItem
										v-if="secondaryDriver"
										class="w-1/2"
										meta-label="Secondary driver"
										:meta-primary="secondaryDriverName"
										:meta-secondary="
											secondaryDriver.mobileNumber
										"
									/>
								</div>

								<SummaryItem
									class="mt-6"
									meta-label="Residential address"
									:meta-primary="residentialAddress.line1"
									:meta-secondary="residentialAddress.line2"
								/>
							</div>

							<div v-if="plan">
								<SummaryItem
									meta-label="Subscription plan"
									:meta-primary="planName"
									:meta-secondary="plan.discount?.description"
								/>
							</div>

							<div v-if="delivery?.delivery_date">
								<div class="flex flex-col gap-6 sm:flex-row">
									<SummaryItem
										class="w-1/2"
										meta-label="Delivery details"
										:meta-primary="pickupOfficeName"
										:meta-secondary="delivery.address"
									/>
									<SummaryItem
										class="w-1/2"
										meta-label="Pick up date"
										:meta-primary="
											formatDate(delivery.delivery_date)
										"
									/>
								</div>
							</div>

							<div v-if="summary">
								<SummaryItem
									v-if="summary.insurance_excess !== null"
									meta-label="Insurance excess"
									:meta-primary="
										formatCurrency(summary.insurance_excess)
									"
								/>

								<div
									class="mt-6 border-b border-t border-dashed border-gray-500 py-6"
								>
									<h4 class="title">
										Amount charged upon delivery
									</h4>

									<SummaryItem
										meta-label="4 weeks in advance"
										:original-price="
											getOriginalPrice(
												summary.advance_payment_fee
											)
										"
										:meta-primary="
											formatCurrency(
												summary.advance_payment_fee
													?.total
											)
										"
									/>
									<SummaryPromotion
										v-for="promotion in promotionsUnder(
											'ADVANCE_PAYMENT_FEE'
										)"
										:key="promotion.code"
										:promotion="promotion"
									/>

									<h4 class="title mt-6">
										Total payable today
									</h4>

									<SummaryItem
										meta-label="Upfront fee"
										:original-price="
											getOriginalPrice(
												summary.upfront_fee
											)
										"
										:meta-primary="
											formatCurrency(
												summary.upfront_fee?.total
											)
										"
									/>
									<SummaryPromotion
										v-for="promotion in promotionsUnder(
											'UPFRONT_FEE'
										)"
										:key="promotion.code"
										:promotion="promotion"
									/>

									<template v-if="hasNewCarLevy">
										<SummaryItem
											class="mt-4"
											meta-label="New car levy"
											:original-price="
												getOriginalPrice(
													summary.new_car_levy
												)
											"
											:meta-primary="
												formatCurrency(
													summary.new_car_levy?.total
												)
											"
										/>
										<SummaryPromotion
											v-for="promotion in promotionsUnder(
												'NEW_CAR_LEVY'
											)"
											:key="promotion.code"
											:promotion="promotion"
										/>
									</template>
								</div>
							</div>
						</div>
					</div>
				</div>

				<button
					type="button"
					class="flex w-full items-center justify-between gap-2 text-left text-base font-normal text-gray-600 sm:hidden"
					:aria-expanded="isExpanded"
					aria-controls="order-summary-details"
					@click="isExpanded = !isExpanded"
				>
					<span>
						{{ isExpanded ? 'Hide details' : 'View more details' }}
					</span>
					<IconArrowDown
						class="text-gray-600 duration-200"
						:class="{ 'rotate-180': isExpanded }"
					/>
				</button>
			</template>
		</div>
	</section>
</template>

<script setup lang="ts">
import IconArrowDown from '@/assets/svg/common/ico-arrow_down.svg'
import SummaryItem from '@/components/order-checkout/SummaryItem.vue'
import SummaryPromotion from '@/components/order-checkout/SummaryPromotion.vue'
import { useOrderCheckout } from '@/stores/order-checkout'
import type { CheckoutCharge, CheckoutChargeKey } from '@/types/order-checkout'

const orderCheckout = useOrderCheckout()

const checkout = computed(() => orderCheckout.checkout)
const driver = computed(() => orderCheckout.checkout?.driver)
const secondaryDriver = computed(() => orderCheckout.secondaryDriver)
const plan = computed(() => orderCheckout.checkout?.plan)
const delivery = computed(() => orderCheckout.checkout?.delivery)
const summary = computed(() => orderCheckout.summary)

const isExpanded = ref(false)

const hasCollapsibleDetails = computed(
	() =>
		Boolean(driver.value?.first_name) ||
		Boolean(plan.value) ||
		Boolean(delivery.value?.delivery_date) ||
		Boolean(summary.value)
)

const primaryDriverName = computed(() => {
	const { first_name, middle_name, last_name } = driver.value ?? {}

	return [first_name, middle_name, last_name].filter(Boolean).join(' ')
})

const secondaryDriverName = computed(() => {
	const { firstName, middleName, lastName } = secondaryDriver.value ?? {}

	return [firstName, middleName, lastName].filter(Boolean).join(' ')
})

// Residential address
const residentialAddress = computed(() => {
	const { address, suburb, state, postcode } = driver.value ?? {}

	return {
		line1: address || '',
		line2: [suburb, state?.toUpperCase(), postcode]
			.filter(Boolean)
			.join(' '),
	}
})

// Subscription plan - named from the vehicle's plans once the plan step has loaded them
const planName = computed(() => {
	if (orderCheckout.selectedServicePlan) {
		return orderCheckout.selectedServicePlan.name
	}

	return plan.value?.term
		? `Minimum term ${plan.value.term} months`
		: 'Standard subscription'
})

const hasNewCarLevy = computed(() =>
	Boolean(summary.value?.new_car_levy?.total)
)

// Shown crossed out next to a discounted price
const getOriginalPrice = (charge: CheckoutCharge | null | undefined) =>
	charge?.discount ? formatCurrency(charge.amount) : null

// Under the first line the promo discounts; a promo without one here (e.g. on the weekly fee) under the upfront fee
const promotionsUnder = (line: CheckoutChargeKey) => {
	const shownLines: CheckoutChargeKey[] = hasNewCarLevy.value
		? ['ADVANCE_PAYMENT_FEE', 'UPFRONT_FEE', 'NEW_CAR_LEVY']
		: ['ADVANCE_PAYMENT_FEE', 'UPFRONT_FEE']

	return orderCheckout.promotions.filter(
		(promotion) =>
			(promotion.targets.find((target) => shownLines.includes(target)) ??
				'UPFRONT_FEE') === line
	)
}

const pickupOfficeName = computed(() => {
	const office = orderCheckout.pickupLocations.find(
		(location) => location.state === delivery.value?.state
	)

	return office?.name
		? `${office.name} office`
		: (delivery.value?.state?.toUpperCase() ?? null)
})
</script>
